import { OnPageConnector, DataInputOutput, Database, Decision, Delay, diffNumericOptions, DirectAccessStorage, Display, Document, Terminator, Process, Extract, Image, ManualInputOutput, ManualOperation, Merge, MultipleDocuments, LogicalOr, Path, PredefinedProcess, Preparation, SummingJunction, Rectangle, Circle, TextBlock, InternalStorage, OffPageConnector, DataStorage, Sort, Collate } from './../svg';
import { Intersect, Point, randomId, Rect, RectAlign } from './../math';
import { defined, contains, isDefined, isFunction, isString, isUndefined, remove } from './../utils';
import { closestConnector, connectors, filterShapeDataItem, translateToOrigin } from './utils';
import { deepExtend } from './../utils/deep-extend';
import { ABSOLUTE, HEIGHT, ITEMBOUNDSCHANGE, ITEMROTATE, NO_WRAP, ROTATED, TRANSFORMED, WIDTH, X, Y, FOCUS_CLASS, CLASS_NAME, EMPTY, SHAPE } from './../constants';
import { RotateUnit } from './../services/RotateUnit';
import { Connector } from './Connector';
import { DefaultConnectors, shapeDefaults } from './shapeDefaults';
import { DiagramElement } from './DiagramElement';
import { TemplateService } from '../services';
const shapes = {
    Terminator: Terminator,
    Process: Process,
    Decision: Decision,
    PredefinedProcess: PredefinedProcess,
    Document: Document,
    MultipleDocuments: MultipleDocuments,
    ManualInputOutput: ManualInputOutput,
    Preparation: Preparation,
    ManualOperation: ManualOperation,
    InternalStorage: InternalStorage,
    Display: Display,
    DirectAccessStorage: DirectAccessStorage,
    Database: Database,
    OnPageConnector: OnPageConnector,
    OffPageConnector: OffPageConnector,
    DataInputOutput: DataInputOutput,
    SummingJunction: SummingJunction,
    LogicalOr: LogicalOr,
    Merge: Merge,
    Extract: Extract,
    DataStorage: DataStorage,
    Delay: Delay,
    Sort: Sort,
    Collate: Collate
};
export class Shape extends DiagramElement {
    /**
     * Creates a new Shape instance.
     * @param options Configuration options for the shape
     * @param diagram The diagram that will contain this shape
     */
    constructor(options, diagram) {
        super(options);
        /** @hidden */
        this.name = SHAPE;
        // Store original width/height if explicitly provided to preserve them during save
        this._originalWidth = options === null || options === void 0 ? void 0 : options.width;
        this._originalHeight = options === null || options === void 0 ? void 0 : options.height;
        this.options = deepExtend({}, this.options, shapeDefaults({ connectors: undefined }), options);
        if (this.options.type === 'text') {
            this.options.content.textWrap = NO_WRAP;
        }
        this.diagram = diagram;
        this.updateOptionsFromModel();
        this.connectors = [];
        this.type = this.options.type;
        this.createShapeVisual();
        this.updateBounds();
        this.content(this.content());
        this._createConnectors();
    }
    /** @hidden */
    _setOptionsFromModel(model) {
        const modelOptions = filterShapeDataItem(model || this.dataItem);
        this.options = deepExtend({}, this.options, modelOptions);
        this.redrawVisual();
    }
    /**
     * Updates the shape options from the model data.
     * @param model The model data to extract options from
     * @param field Specific field to update, if any
     */
    updateOptionsFromModel(model, field) {
        if (this.diagram && this.diagram._isEditable) {
            const modelOptions = filterShapeDataItem(model || this.dataItem);
            if (model && field) {
                if (!contains(['x', 'y', 'width', 'height'], field)) {
                    if (this.options.visual) {
                        this._redrawVisual();
                    }
                    else if (modelOptions.type) {
                        this.options = deepExtend({}, this.options, modelOptions);
                        this._redrawVisual();
                    }
                    if (this.options.content) {
                        this._template();
                        this.content(this.options.content);
                    }
                }
                else {
                    const bounds = this.bounds();
                    bounds[field] = model[field];
                    this.bounds(bounds);
                }
            }
            else {
                this.options = deepExtend({}, this.options, modelOptions);
            }
        }
    }
    /** @hidden */
    _redrawVisual() {
        this.visual.clear();
        this._contentVisual = null;
        this.options.dataItem = this.dataItem;
        this.createShapeVisual();
        this.updateBounds();
    }
    /**
     * Redraws the shape's visual representation.
     * Updates both the visual and content if present.
     */
    redrawVisual() {
        this._redrawVisual();
        if (this.options.content) {
            this._template();
            this.content(this.options.content);
        }
    }
    /**
     * Updates the shape's model data and optionally synchronizes changes.
     * @param syncChanges Whether to synchronize changes immediately
     */
    updateModel(syncChanges) {
        const diagram = this.diagram;
        if (diagram && diagram._isEditable) {
            diagram.updateShapeModel(this, syncChanges);
        }
    }
    /**
     * Updates the shape's bounds based on its visual representation and handles rotation and content alignment.
     */
    updateBounds() {
        const bounds = this.visual._measure(true);
        const options = this.options;
        // Use measured dimensions for internal bounds (includes stroke width for correct rendering)
        this.bounds(new Rect(options.x, options.y, bounds.width, bounds.height));
        this._rotate();
        this._alignContent();
    }
    /**
     * Gets or sets the content of the shape.
     * @param content The content to set. If not provided, returns the current content.
     * @returns The current content when used as a getter
     */
    content(content) {
        const result = this._content(content);
        this._alignContent();
        return result;
    }
    /** @hidden */
    _alignContent() {
        const contentOptions = this.options.content || {};
        const contentVisual = this._contentVisual;
        if (contentVisual && contentOptions.align && contentVisual.alignable !== false) {
            const containerRect = this.visual._measure();
            const aligner = new RectAlign(containerRect);
            const contentBounds = contentVisual.drawingElement.bbox(null);
            const contentRect = new Rect(0, 0, contentBounds.width(), contentBounds.height());
            const alignedBounds = aligner.align(contentRect, contentOptions.align);
            contentVisual.position(alignedBounds.topLeft());
        }
    }
    /** @hidden */
    _createConnectors() {
        const options = this.options, currentConnectors = options.connectors || connectors[options.type] || DefaultConnectors, length = currentConnectors.length, connectorDefaults = options.connectorDefaults;
        let connector, i;
        for (i = 0; i < length; i++) {
            connector = new Connector(this, deepExtend({}, connectorDefaults, currentConnectors[i]));
            this.connectors.push(connector);
        }
    }
    /**
     * Gets or sets the bounds of the shape.
     * @param value The bounds to set, or a string constant for different bound types ('TRANSFORMED', 'ABSOLUTE', 'ROTATED'). If not provided, returns the current bounds.
     * @returns The bounds of the shape
     */
    bounds(value) {
        let bounds;
        if (value) {
            if (isString(value)) {
                switch (value) {
                    case TRANSFORMED: {
                        bounds = this._transformedBounds();
                        break;
                    }
                    case ABSOLUTE: {
                        bounds = this._transformedBounds();
                        const pan = this.diagram._pan;
                        bounds.x += pan.x;
                        bounds.y += pan.y;
                        break;
                    }
                    case ROTATED: {
                        bounds = this._rotatedBounds();
                        break;
                    }
                    default: {
                        bounds = this._bounds;
                        break;
                    }
                }
            }
            else {
                this._setBounds(value);
                this._triggerBoundsChange();
                if (!(this.diagram && this.diagram._layouting)) {
                    this.refreshConnections();
                }
            }
        }
        else {
            bounds = this._bounds;
        }
        return bounds;
    }
    /** @hidden */
    _setBounds(rect) {
        const options = this.options;
        const topLeft = rect.topLeft();
        const x = options.x = topLeft.x;
        const y = options.y = topLeft.y;
        const width = options.width = Math.max(rect.width, options.minWidth);
        const height = options.height = Math.max(rect.height, options.minHeight);
        this._bounds = new Rect(x, y, width, height);
        this.visual.redraw({
            x: x,
            y: y,
            width: width,
            height: height
        });
    }
    /**
     * Gets or sets the position of the shape.
     * @param point The position to set. If not provided, returns the current position.
     * @returns The current position when used as a getter
     */
    position(point) {
        if (point) {
            this.bounds(new Rect(point.x, point.y, this._bounds.width, this._bounds.height));
        }
        else {
            return this._bounds.topLeft();
        }
    }
    /**
     * Returns a clone of this shape.
     * @returns {Shape} A new Shape instance that is a copy of this shape
     */
    clone() {
        const json = this.serialize();
        json.options.id = randomId();
        if (this.diagram && this.diagram._isEditable && defined(this.dataItem)) {
            json.options.dataItem = this.diagram.options.cloneDataItem(this.dataItem);
        }
        return new Shape(json.options, undefined);
    }
    /**
     * Selects or deselects the shape.
     * @param value Whether to select (true) or deselect (false) the shape. If not provided, defaults to true.
     * @returns True if the selection state changed, false otherwise
     */
    select(value) {
        const diagram = this.diagram;
        let selected, deselected;
        if (isUndefined(value)) {
            value = true;
        }
        if (this._canSelect()) {
            if (this.isSelected !== value) {
                selected = [];
                deselected = [];
                this.isSelected = value;
                if (this.isSelected) {
                    diagram._selectedItems.push(this);
                    selected.push(this);
                }
                else {
                    remove(diagram._selectedItems, this);
                    deselected.push(this);
                }
                if (!diagram._internalSelection) {
                    diagram._selectionChanged(selected, deselected);
                }
                return true;
            }
        }
    }
    /**
     * Rotates the shape by the specified angle.
     * @param angle The rotation angle in degrees. If not provided, returns the current rotation.
     * @param center The center point for rotation. If not provided, uses the shape's center.
     * @param undoable Whether the rotation should be undoable. Defaults to true.
     * @returns The current rotation when used as a getter
     */
    rotate(angle, center, undoable) {
        const rotate = this.visual.rotate();
        if (angle !== undefined) {
            if (undoable !== false && this.diagram && this.diagram.undoRedoService && angle !== rotate.angle) {
                this.diagram.undoRedoService.add(new RotateUnit(this.diagram._resizingAdorner, [this], [rotate.angle]), false);
            }
            const b = this.bounds(), sc = new Point(b.width / 2, b.height / 2);
            let deltaAngle, newPosition;
            if (center) {
                deltaAngle = angle - rotate.angle;
                newPosition = b.center().rotate(deltaAngle, center).minus(sc);
                this._rotationOffset = this._rotationOffset.plus(newPosition.minus(b.topLeft()));
                this.position(newPosition);
            }
            this.visual.rotate(angle, sc);
            this.options.rotation.angle = angle;
            if (this.diagram && this.diagram._connectorsAdorner) {
                this.diagram._connectorsAdorner.refresh();
            }
            this.refreshConnections();
            if (this.diagram) {
                this.diagram.trigger(ITEMROTATE, { item: this });
            }
        }
        return rotate;
    }
    /**
     * Gets connections of this shape based on type.
     * @param type The type of connections to retrieve: 'in' for incoming, 'out' for outgoing, or undefined for all connections.
     * @returns Array of connections
     */
    connections(type) {
        const result = [];
        let i, j, con, cons, ctr;
        for (i = 0; i < this.connectors.length; i++) {
            ctr = this.connectors[i];
            cons = ctr.connections;
            for (j = 0, cons; j < cons.length; j++) {
                con = cons[j];
                if (type === 'out') {
                    const source = con.source();
                    if (source.shape && source.shape === this) {
                        result.push(con);
                    }
                }
                else if (type === 'in') {
                    const target = con.target();
                    if (target.shape && target.shape === this) {
                        result.push(con);
                    }
                }
                else {
                    result.push(con);
                }
            }
        }
        return result;
    }
    /**
     * Refreshes all connections attached to this shape.
     * Updates the visual representation of all connected connections.
     */
    refreshConnections() {
        this.connections().forEach(function (connection) {
            connection.refresh();
        });
    }
    /**
     * Gets a connector of this shape either by the connector's supposed name or
     * via a Point in which case the closest connector will be returned.
     *
     * @param nameOrPoint The name of a Connector or a Point.
     * @returns {Connector} The connector matching the name or closest to the point
     */
    getConnector(nameOrPoint) {
        let i, ctr;
        if (isString(nameOrPoint)) {
            nameOrPoint = nameOrPoint.toLocaleLowerCase();
            for (i = 0; i < this.connectors.length; i++) {
                ctr = this.connectors[i];
                if (ctr.options.name.toLocaleLowerCase() === nameOrPoint) {
                    return ctr;
                }
            }
        }
        else if (nameOrPoint instanceof Point) {
            return closestConnector(nameOrPoint, this.connectors);
        }
        else {
            return this.connectors.length ? this.connectors[0] : null;
        }
    }
    /**
     * Gets the position of the shape at a specific side.
     * @param side The side to get position for (e.g., 'top', 'bottom', 'left', 'right', 'center')
     * @returns The position point at the specified side
     */
    getPosition(side) {
        const b = this.bounds(), fnName = side.charAt(0).toLowerCase() + side.slice(1);
        if (isFunction(b[fnName])) {
            return this._transformPoint(b[fnName]());
        }
        return b.center();
    }
    /**
     * Redraws the shape with new options.
     * @param options The options to apply when redrawing the shape
     */
    redraw(options) {
        if (options) {
            let shapeOptions = this.options;
            let boundsChange;
            this.shapeVisual.redraw(this._visualOptions(options));
            if (this._diffNumericOptions(options, [WIDTH, HEIGHT, X, Y])) {
                this.bounds(new Rect(shapeOptions.x, shapeOptions.y, shapeOptions.width, shapeOptions.height));
                boundsChange = true;
            }
            if (options.connectors) {
                shapeOptions.connectors = options.connectors;
                this._updateConnectors();
            }
            shapeOptions = deepExtend(shapeOptions, options);
            if (options.rotation || boundsChange) {
                this._rotate();
            }
            if (shapeOptions.content) {
                this.content(shapeOptions.content);
            }
        }
    }
    /** @hidden */
    _updateConnectors() {
        const connections = this.connections();
        this.connectors = [];
        this._createConnectors();
        let connection;
        let source;
        let target;
        for (let idx = 0; idx < connections.length; idx++) {
            connection = connections[idx];
            source = connection.source();
            target = connection.target();
            if (source.shape && source.shape === this) {
                connection.source(this.getConnector(source.options.name) || null);
            }
            else if (target.shape && target.shape === this) {
                connection.target(this.getConnector(target.options.name) || null);
            }
            connection.updateModel();
        }
    }
    /** @hidden */
    _diffNumericOptions(options, fields) {
        return diffNumericOptions.call(this, options, fields);
    }
    /** @hidden */
    _visualOptions(options) {
        return {
            accessibility: options.accessibility,
            cornerRadius: options.cornerRadius,
            data: options.path,
            source: options.source,
            hover: options.hover,
            fill: options.fill,
            stroke: options.stroke,
            dataItem: this.dataItem || (options === null || options === void 0 ? void 0 : options.dataItem),
            autoSize: options.autoSize,
            id: options.id
        };
    }
    /** @hidden */
    _triggerBoundsChange() {
        if (this.diagram) {
            this.diagram.trigger(ITEMBOUNDSCHANGE, { item: this, bounds: this._bounds.clone() }); // the trigger modifies the arguments internally.
        }
    }
    /** @hidden */
    _transformPoint(point) {
        const rotate = this.rotate(), bounds = this.bounds(), tl = bounds.topLeft();
        if (rotate.angle) {
            point.rotate(rotate.angle, rotate.center().plus(tl));
        }
        return point;
    }
    /** @hidden */
    _transformedBounds() {
        const bounds = this.bounds(), tl = bounds.topLeft(), br = bounds.bottomRight();
        return Rect.fromPoints(this.diagram.modelToView(tl), this.diagram.modelToView(br));
    }
    /** @hidden */
    _rotatedBounds() {
        const bounds = this.bounds().rotatedBounds(this.rotate().angle), tl = bounds.topLeft(), br = bounds.bottomRight();
        return Rect.fromPoints(tl, br);
    }
    /** @hidden */
    _rotate() {
        const rotation = this.options.rotation;
        if (rotation && rotation.angle) {
            this.rotate(rotation.angle);
        }
        this._rotationOffset = new Point();
    }
    /** @hidden */
    _hover(value) {
        const options = this.options, hover = options.hover;
        let stroke = options.stroke, fill = options.fill;
        if (value && isDefined(hover.stroke)) {
            stroke = deepExtend({}, stroke, hover.stroke);
        }
        if (value && isDefined(hover.fill)) {
            fill = hover.fill;
        }
        this.shapeVisual.redraw({
            stroke: stroke,
            fill: fill
        });
        if (options.editable && options.editable.connect) {
            this.diagram._showConnectors(this, value);
        }
    }
    /** @hidden */
    _getMaxConnectorOffset() {
        var _a, _b, _c;
        let maxOffset = 0;
        for (let i = 0; i < this.connectors.length; i++) {
            const connector = this.connectors[i];
            const offset = connector.options.offset || 0;
            const connectorSize = Math.max(connector.options.width || 0, connector.options.height || 0);
            const strokeWidth = ((_a = connector.options.stroke) === null || _a === void 0 ? void 0 : _a.width) || 0;
            const hoverStrokeWidth = ((_c = (_b = connector.options.hover) === null || _b === void 0 ? void 0 : _b.stroke) === null || _c === void 0 ? void 0 : _c.width) || strokeWidth;
            // Total distance needed: offset + connector radius + max stroke width
            const totalOffset = offset + connectorSize / 2 + Math.max(strokeWidth, hoverStrokeWidth);
            if (totalOffset > maxOffset) {
                maxOffset = totalOffset;
            }
        }
        return maxOffset;
    }
    /** @hidden */
    _hitTest(value) {
        if (this.visible()) {
            let bounds = this.bounds();
            const angle = this.rotate().angle;
            const maxOffset = this._getMaxConnectorOffset();
            let rotatedPoint;
            // Expand bounds to include connector offsets and sizes for hover detection
            if (maxOffset > 0) {
                bounds = bounds.clone();
                bounds.inflate(maxOffset, maxOffset);
            }
            if (value.isEmpty && !value.isEmpty()) { // rect selection
                return Intersect.rects(value, bounds, angle ? angle : 0);
            }
            else { // point
                rotatedPoint = value.clone().rotate(angle, bounds.center()); // cloning is important because rotate modifies the point inline.
                if (bounds.contains(rotatedPoint)) {
                    return this;
                }
            }
        }
    }
    /**
     * Converts the shape to a JSON representation for serialization.
     * @returns Object containing the shape's ID
     */
    toJSON() {
        return {
            shapeId: this.options.id
        };
    }
    /**
     * Creates the visual representation of the shape based on its type and options.
     * Supports custom templates, predefined shapes (rectangle, circle, text, image), and custom paths.
     */
    createShapeVisual() {
        const options = this.options;
        const visualOptions = this._visualOptions(options);
        const visualTemplate = options.visual ? TemplateService.compile(options.visual, options) : options.visual;
        const type = (options.type + '').toLocaleLowerCase();
        let shapeVisual;
        visualOptions.width = options.width;
        visualOptions.height = options.height;
        if (isFunction(visualTemplate)) { // custom template
            shapeVisual = visualTemplate.call(this, options);
        }
        else if (visualOptions.data) {
            shapeVisual = new Path(visualOptions);
            translateToOrigin(shapeVisual);
        }
        else if (type === 'rectangle') {
            shapeVisual = new Rectangle(visualOptions);
        }
        else if (type === 'circle') {
            shapeVisual = new Circle(visualOptions);
        }
        else if (type === 'text') {
            shapeVisual = new TextBlock(visualOptions);
        }
        else if (type === 'image') {
            shapeVisual = new Image(visualOptions);
        }
        else if (shapes[options.type]) {
            shapeVisual = new shapes[options.type](visualOptions);
        }
        else {
            shapeVisual = new Path(visualOptions);
        }
        this.shapeVisual = shapeVisual;
        this.visual.append(this.shapeVisual);
    }
    /** @hidden */
    focus() {
        this.shapeVisual.drawingElement.options.set(CLASS_NAME, FOCUS_CLASS);
    }
    /** @hidden */
    blur() {
        this.shapeVisual.drawingElement.options.set(CLASS_NAME, EMPTY);
    }
}
